import { Suspense, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { GlobalSearch } from './GlobalSearch';
import { useNavBadges, useSearchAccess, useSetupStatus } from '../../features/shell/hooks';
import { HelpButton } from './HelpButton';
import { NotificationBell } from './NotificationBell';
import { Icon } from '../ui/Icon';
import { Spinner } from '../ui/misc';
import { useAuth, usePermissions, useSession } from '../../features/auth/useAuth';
import { useEmployeeFormMetadata } from '../../features/employees/hooks';
import { ProcessAttendanceDialog } from '../../features/attendance/ProcessAttendanceDialog';
import { cn } from '../../lib/cn';
import { properCase } from '../../lib/format';
import { ZoneClock } from './ZoneClock';
import { showSetupCard } from '../../features/shell/setupMode';
import { groupedNav, navBadgeId, visibleNav, type NavItem } from './nav';
import { NavBadge } from './NavBadge';

export function AppShell() {
  const session = useSession();
  const permissions = usePermissions();
  const { logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  // `00-dashboard-implementation-plan.md` Phase 2.1 — `POST /attendance/process`
  // already exists and is idempotent (attendance-processing.service.ts's own
  // doc comment); this reuses the same dialog `AttendancePage.tsx` already
  // opens from its own header button, rather than firing a blind mutation
  // with an invented default date window.
  const [processing, setProcessing] = useState(false);
  const canProcessAttendance = permissions.can('attendance.adjust');
  const items = visibleNav(permissions);
  const groups = groupedNav(items);
  const badges = useNavBadges();
  const location = useLocation();
  const pageTitle = pageTitleFor(items, location.pathname);
  const search = useSearchAccess();
  const displayName = properCase(session.display_name);

  // `06-ui-ux.md` task 6.7 — "currently invisible which branch you're
  // scoped to." A real per-page branch SWITCHER would need every list/report
  // page to accept a selected-branch filter, which nothing in the app does
  // today (a branch-scoped user — BMS — already sees every one of their
  // assigned branches merged, via server-side scope filtering, not a
  // client-side selection) — that's real work beyond this task, deferred
  // rather than shipped as a dropdown that doesn't actually filter anything.
  // What's genuinely missing, and what this fixes, is the *visibility* the
  // task title names: a read-only label naming the branch(es) a scoped
  // session actually covers. `GET /employees/form-metadata` already scopes
  // its own `branches` list to the caller's assigned branches for a
  // branch-scoped session (ADR-13A-005) — reused here rather than a new
  // endpoint. Tenant-wide roles see every branch, so there's nothing
  // ambiguous to label for them.
  const isBranchScoped = session.branch_ids.length > 0 && permissions.can('employee.view');
  const meta = useEmployeeFormMetadata(isBranchScoped);
  const scopedBranchNames = useMemo(() => {
    if (!isBranchScoped || !meta.data) return null;
    const names = meta.data.branches
      .filter((b) => session.branch_ids.includes(b.branch_id))
      .map((b) => b.name);
    return names.length > 0 ? names.join(', ') : null;
  }, [isBranchScoped, meta.data, session.branch_ids]);

  return (
    <div className="flex min-h-screen bg-canvas">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-line bg-surface md:flex">
        {/* Brand block — Stage 5 UI Shell, `00a-component-library/code.html`
         * §11's tenant sidebar. "HR Clock"/"MERVONN WORKFORCE" is the
         * product/operator identity; the tenant's own name lives in the
         * context card below, not here. */}
        <div className="flex items-center gap-2.5 border-b border-line p-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-tight bg-brand text-sm font-bold text-white">
            HC
          </div>
          <div className="min-w-0 leading-none">
            <div className="truncate text-xs font-bold text-ink">HR Clock</div>
            <div className="mt-0.5 truncate text-[11px] text-ink-muted">MERVONN WORKFORCE</div>
          </div>
        </div>

        {/* Tenant-context card — same data/logic as before, just relocated
         * out of the header (see the header comment below). */}
        <div className="mx-3 mt-3 flex items-center gap-2 rounded-tight border border-line bg-canvas/60 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-ink">{session.tenant_name}</p>
            {scopedBranchNames && (
              <p className="truncate text-[11px] text-ink-subtle">{scopedBranchNames}</p>
            )}
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto p-2" aria-label="Main">
          {groups.map((g) => (
            <div key={g.group} className="flex flex-col gap-0.5">
              <p className="px-3 pb-1 pt-1 text-xs font-medium text-ink-subtle">
                {g.label}
              </p>
              {g.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  aria-describedby={item.badge && badges[item.badge] ? navBadgeId(item) : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-brand text-white' : 'text-ink-muted hover:bg-sunken hover:text-ink',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon name={item.icon} className={cn('text-[18px]', isActive && 'text-pulse')} />
                      <span className="flex-1">{item.label}</span>
                      <NavBadge item={item} badges={badges} />
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <SidebarStatusCard />

        {/* User-profile footer card — display only. The interactive
         * sign-out menu stays in the header (see below); this mirrors the
         * reference's footer card without duplicating that control. */}
        <div className="flex items-center gap-2 border-t border-line bg-canvas/60 p-3">
          <span
            aria-hidden
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white"
          >
            {initials(displayName)}
          </span>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs font-medium text-ink">{displayName}</p>
            <p className="truncate text-[11px] text-ink-subtle">{session.role_name}</p>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between gap-3 border-b border-line bg-surface px-4">
          {/* Tenant name/branch live in the sidebar's context card. This slot is
           * the header search (Stage 7 Phase 2A) — shown only to roles that can
           * view employees or devices, each behind its own permission. */}
          <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-1.5 text-sm md:flex">
            <span className="truncate text-ink-muted">{session.tenant_name}</span>
            <span aria-hidden className="text-ink-subtle">›</span>
            <span className="truncate font-display font-semibold text-ink">{pageTitle}</span>
          </nav>
          <div className="hidden min-w-0 flex-1 items-center gap-2 lg:flex">
            {search.any && <GlobalSearch canEmployees={search.canEmployees} canDevices={search.canDevices} />}
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-3">
            <ZoneClock />

            <div className="hidden items-center gap-1 sm:flex">
              <NotificationBell />
              <HelpButton />
            </div>

            <div className="hidden h-4 w-px bg-line sm:block" />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                aria-label="Account menu"
                className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-ink hover:bg-sunken">
                <span
                  aria-hidden
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white"
                >
                  {initials(displayName)}
                </span>
                {/* task 6.8 — the role moves here, next to the name, instead of
                 * faint gray sidebar-footer text nobody's eye lands on. */}
                <span className="hidden flex-col items-start leading-tight sm:flex">
                  <span>{displayName}</span>
                  <span className="text-xs text-ink-subtle">{session.role_name}</span>
                </span>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="z-50 min-w-52 rounded-md border border-line bg-surface p-1 shadow-raised"
                >
                  <div className="px-2 py-1.5 text-xs text-ink-subtle">{session.email}</div>
                  <DropdownMenu.Separator className="my-1 h-px bg-line" />
                  <DropdownMenu.Item
                    disabled={signingOut}
                    onSelect={(e) => {
                      e.preventDefault();
                      setSigningOut(true);
                      void logout();
                    }}
                    className="cursor-pointer rounded px-2 py-1.5 text-sm text-ink outline-none hover:bg-sunken data-[disabled]:opacity-50"
                  >
                    {signingOut ? 'Signing out…' : 'Sign out'}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </header>

        {/* Mobile nav */}
        <nav aria-label="Main" className="flex gap-1 overflow-x-auto border-b border-line bg-surface px-2 py-1.5 md:hidden">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap rounded px-3 py-1.5 text-sm font-medium',
                  isActive ? 'bg-brand text-white' : 'text-ink-muted',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="mx-auto w-full max-w-7xl flex-1 p-4 sm:p-6">
          <Suspense fallback={<Spinner label="Loading…" />}>
            <Outlet />
          </Suspense>
        </main>

        {/*
         * Phase 2.2 — static content, no backend dependency. The reference's
         * exact wording ("Compliant with Kenyan Employment Act & Statutory
         * Regimes") is an unverifiable legal-compliance claim, the same
         * class of overclaim this project has repeatedly caught and
         * corrected elsewhere (fake "ISO 27001"/"KRA iTax Verified" badges,
         * Phases 3–5's fabricated-compliance-claim pattern). What's actually
         * true and checkable here is narrower and real: the payroll engine
         * does compute these four statutory deduction types (`DeductionType`
         * enum, `statutory_rule_versions`) — stated as that, not as a
         * sweeping "compliant with the Act" certification nobody verified.
         */}
        <footer className="border-t border-line bg-surface px-4 py-3 text-center text-xs text-ink-subtle sm:px-6">
          HR Clock v1.0 · Statutory payroll deductions: PAYE · NSSF · SHIF · AHL
        </footer>
      </div>

      {canProcessAttendance && (
        <ProcessAttendanceDialog open={processing} onOpenChange={setProcessing} />
      )}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

function pageTitleFor(items: NavItem[], pathname: string): string {
  const match = items
    .filter((i) => (i.path === '/' ? pathname === '/' : pathname === i.path || pathname.startsWith(i.path + '/')))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.label ?? 'Dashboard';
}

/** Setup progress while an admin's tenant is still being set up. */
function SidebarStatusCard() {
  const permissions = usePermissions();
  const setup = useSetupStatus(permissions.can('tenant.edit'));
  return showSetupCard(setup.data) ? <SetupCard done={setup.data!.done} total={setup.data!.total} /> : null;
}

function SetupCard({ done, total }: { done: number; total: number }) {
  return (
    <Link to="/" className="mx-3 mb-3 block rounded-tight border border-line bg-surface-subtle p-3">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-ink">Setup</span>
        <span className="font-mono tabular-nums text-ink-muted">
          {done}/{total}
        </span>
      </div>
      <div
        className="mt-2 flex gap-1"
        role="img"
        aria-label={`Setup progress: ${done} of ${total} steps done`}
      >
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={cn('h-1.5 flex-1 rounded-full', i < done ? 'bg-positive' : 'bg-sunken')} />
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-ink-subtle">Finish setup to start tracking attendance</p>
    </Link>
  );
}
